import { styled } from "../shared/Template.styles";
import {
    useDeactivateUserMutation,
    useGetUserByIdQuery,
    useReactivateUserMutation,
    useUpdateUserMutation,
} from "@/src/graphql/generated/api/graphql";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import ConfirmDialog from "../../organisms/ConfirmDialog/ConfirmDialog";
import { useAuth } from "react-oidc-context";
import formatDateAndTime from "@/src/lib/formatDateAndTime/formatDateAndTime";
import SelectRole from "@/src/components/organisms/SelectRole/SelectRole";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

const UserDetailTemplate = () => {
    const { t } = useTranslation("common", {
        keyPrefix: "user-detail",
    });

    const auth = useAuth();
    const loggedInUserEmail = auth.user?.profile.email;

    const { id: userId } = useParams({ from: "/admin/users/$id" });
    const [loading, setLoading] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [confirmedDeletion, setConfirmedDeletion] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();

    const {
        data,
        loading: queryLoading,
        error,
    } = useGetUserByIdQuery({
        variables: { id: userId },
    });

    const { hasAnyPermission, hasPermission } = usePermissions();

    const schema = yup
        .object({
            user: yup.object().shape({
                role: yup.string().required(),
            }),
        })
        .required();

    const form = useForm<yup.InferType<typeof schema>>({
        resolver: yupResolver(schema),
        reValidateMode: "onChange",
        mode: "onChange",
    });

    const formUser = form.watch("user");

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarOpen(false);
    };

    const onSubmit = form.handleSubmit(
        () => {
            setLoading(true);
            updateUserMutation();
        },
        (_error) => {
            setErrorMessage(_error.user?.role?.message ?? "validation-error");
            setSnackbarOpen(true);
        }
    );

    const [deleteUser, { loading: deleteLoading }] = useDeactivateUserMutation({
        variables: {
            id: userId,
        },
        refetchQueries: ["GetUsers"],
        onCompleted: () => {
            navigate({ to: "/admin/users" });
        },
        onError: (error) => {
            console.error("Error deactivating user:", error);
            setConfirmedDeletion(false);
        },
    });

    const [reactivateUser, { loading: reactivateLoading }] =
        useReactivateUserMutation({
            variables: {
                id: userId,
            },
            refetchQueries: ["GetUsers"],
            onCompleted: () => {
                navigate({ to: "/admin/users" });
            },
        });

    useEffect(() => {
        if (confirmedDeletion) {
            deleteUser().finally();
        }
    }, [confirmedDeletion, deleteUser]);

    const [updateUserMutation] = useUpdateUserMutation({
        variables: {
            input: {
                id: userId,
                roleId: formUser?.role ?? data?.getUser?.role?.id,
                enabled: data?.getUser?.isEnabled || false,
            },
        },
        refetchQueries: ["GetUsers"],
        onCompleted: () => {
            setLoading(false);
            navigate({ to: "/admin/users" });
        },
        onError: (_error) => {
            setLoading(false);
            setErrorMessage(t("update-failed"));
            setSnackbarOpen(true);
        },
    });

    if (queryLoading) return <CircularProgress />;

    if (error || !data?.getUser) {
        console.error("Error fetching user detail:", error);
        return <div>{t("user-not-found", { userId })}</div>;
    }

    const { id, email, firstName, lastName, auditInfo, isEnabled, role } =
        data.getUser;

    const getCreatedByName = () => {
        const firstName = auditInfo?.createdBy?.firstName || "";
        const lastName = auditInfo?.createdBy?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || t("unknown-user");
    };

    const { date, time } = formatDateAndTime(auditInfo?.createdAt || "");

    const handleUserStatusClick = () => {
        if (email === loggedInUserEmail) {
            return;
        }

        if (isEnabled) {
            setShowDeleteDialog(true);
        } else {
            reactivateUser();
        }
    };

    const handleDeleteDialogClose = ({
        confirmed,
    }: {
        confirmed?: boolean;
    }) => {
        setShowDeleteDialog(false);

        if (confirmed && email !== loggedInUserEmail) {
            setConfirmedDeletion(true);
        }
    };

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={t("user-full-name", {
                        fullName: `${firstName} ${lastName}`,
                    })}
                    {...(hasPermission("USERS_DEACTIVATE") && {
                        button: {
                            onClick: handleUserStatusClick,
                            label: (() => {
                                if (deleteLoading || reactivateLoading) {
                                    return isEnabled
                                        ? t("deactivating")
                                        : t("reactivating");
                                }
                                return isEnabled
                                    ? t("deactivate-user")
                                    : t("reactivate-user");
                            })(),
                            color: "error",
                            variant: "contained",
                            disabled:
                                deleteLoading ||
                                reactivateLoading ||
                                email === loggedInUserEmail,
                        },
                    })}
                />
                <styled.Main>
                    <form onSubmit={onSubmit} data-testid="form">
                        <Stack spacing={4} direction="column">
                            <TextField
                                defaultValue={id}
                                disabled
                                label={t("user-id")}
                            />
                            <TextField
                                defaultValue={firstName}
                                disabled
                                label={t("first-name")}
                            />
                            <TextField
                                defaultValue={lastName}
                                disabled
                                label={t("last-name")}
                            />
                            <TextField
                                defaultValue={email}
                                disabled
                                label={t("email")}
                            />
                            <SelectRole
                                {...form.register("user.role")}
                                defaultValue={role?.id}
                                label={t("role")}
                                disabled={!hasPermission("USERS_UPDATE")}
                            />
                            <Typography variant="body2" color="textSecondary">
                                {t("created-at", {
                                    date,
                                    time,
                                })}{" "}
                                {t("by-user", {
                                    fullName: getCreatedByName(),
                                })}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                }}
                            >
                                {hasAnyPermission([
                                    "USERS_UPDATE",
                                    "OWN_USER_UPDATE",
                                ]) && (
                                    <Button
                                        type="submit"
                                        loading={loading}
                                        variant="contained"
                                        color="primary"
                                        data-testid="submit-button"
                                    >
                                        {t("save")}
                                    </Button>
                                )}
                            </Box>
                        </Stack>
                    </form>
                </styled.Main>
            </styled.Container>
            <Snackbar
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
            <ConfirmDialog
                open={showDeleteDialog}
                indicators={[
                    {
                        id: id,
                        name: `${firstName} ${lastName}`,
                    },
                ]}
                onClose={handleDeleteDialogClose}
                translationPrefix="confirm-dialogs.users"
            />
        </>
    );
};

export default UserDetailTemplate;
